

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Avatar,
  Divider,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { updateProfileAction, updateAdminPasswordAction } from '../../redux/actions/authActions';

const ProfilePage = () => {

  const location = useLocation();

  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const isLoading = useSelector((state) => state.auth?.isLoading);
  const isSuccess = useSelector((state) => state.auth?.isSuccess);
  const isError = useSelector((state) => state.auth?.isError);
  // Validation schemas
  const editProfileSchema = Yup.object({
    first_name: Yup.string().max(50, 'Too long').required('First name is required'),
    last_name: Yup.string().max(50, 'Too long').required('Last name is required'),
    mobile_no: Yup.string().matches(/^[0-9]{10,15}$/, 'Enter a valid phone number').required('Phone number is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
  });

  const passwordSchema = Yup.object({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string().min(6, 'Password must be at least 6 characters').required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
      .required('Confirm your new password'),
  });
  // ...existing code...


  if (!userInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography variant="h6">No user information available.</Typography>
      </Box>
    );
  }


  // Get initials for avatar
  const getInitials = () => {
    const first = userInfo.first_name && userInfo.first_name.length > 0 ? userInfo.first_name[0].toUpperCase() : '';
    const last = userInfo.last_name && userInfo.last_name.length > 0 ? userInfo.last_name[0].toUpperCase() : '';
    if (first && last) return first + last;
    if (first) return first;
    if (userInfo.role && userInfo.role.length > 0) return userInfo.role[0].toUpperCase();
    return '?';
  };

  // Tab state
  const [tab, setTab] = useState(0);
  const handleTabChange = (event, newValue) => setTab(newValue);

  // Set initial tab from location.state.tab if present
  useEffect(() => {
    if (location && location.state && typeof location.state.tab === 'number') {
      setTab(location.state.tab);
    }
  }, [location]);

  // Password visibility state (must be top-level, not inside render)
  const [showPassword, setShowPassword] = useState(false);
  // ...existing code...
  return (
    <>
      {/* ToastContainer should be rendered once in your app, e.g. in App.jsx. Not needed here if already present. */}
      <Box sx={{ minHeight: '80vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={6} sx={{ width: '100%', maxWidth: 950, mx: 'auto', borderRadius: 4, p: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', minHeight: 450 }}>
            {/* Left column: Avatar, user info, vertical tabs */}
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
              bgcolor: 'background.paper', borderRight: { md: '1px solid #eee' }, py: 3, px: 2, minWidth: 220, maxWidth: 260
            }}>
              <Avatar sx={{ width: 70, height: 70, bgcolor: 'secondary.main', fontSize: 30, mb: 2, boxShadow: 2 }}>
                {getInitials()}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5,  }}>
                {userInfo.first_name || ''} {userInfo.last_name || ''}
              </Typography>
               <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5,  }}>
                {userInfo.role ? userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1) : ''} 
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.85, mb: 2 }}>
                {userInfo.email || '-'}
              </Typography>
              
              <Tabs
                orientation="vertical"
                value={tab}
                onChange={handleTabChange}
                sx={{ borderRight: 1, borderColor: 'divider', minWidth: 170 }}
                TabIndicatorProps={{ style: { left: 0, width: 3 } }}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="User Details" />
                <Tab label="Edit Profile" />
                <Tab label="Update Password" />
                {/* <Tab label="Settings" /> */}
              </Tabs>
            </Box>
            {/* Right column: Tab content only */}
            <Box sx={{ flex: 1, p: { xs: 2, md: 5 }, display: 'block', alignItems: 'flex-start', justifyContent: 'center' }}>
              {tab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>User Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="First Name" fullWidth value={userInfo.first_name || ''} margin="dense" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Last Name" fullWidth value={userInfo.last_name || ''} margin="dense" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Phone Number" fullWidth value={userInfo.mobile_no || ''} margin="dense" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Role" fullWidth value={userInfo.role ? userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1) : ''} margin="dense" InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Email" fullWidth value={userInfo.email || ''} margin="dense" InputProps={{ readOnly: true }} />
                    </Grid>
                  </Grid>
                </Box>
              )}
              {tab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Edit Profile</Typography>
                  <Formik
                    initialValues={{
                      first_name: userInfo?.first_name || '',
                      last_name: userInfo?.last_name || '',
                      mobile_no: userInfo?.mobile_no || '',
                      email: userInfo?.email || '',
                    }}
                    enableReinitialize
                    validationSchema={editProfileSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                      try {
                        const resultAction = await dispatch(updateProfileAction({
                          first_name: values.first_name,
                          last_name: values.last_name,
                          mobile_no: values.mobile_no
                        }));
                        if (updateProfileAction.fulfilled.match(resultAction)) {
                          toast.success('Profile updated successfully!');
                        } else {
                          toast.error(resultAction.payload || 'Update failed');
                        }
                      } catch (err) {
                        toast.error('Update failed');
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting, dirty }) => (
                      <Form>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="First Name"
                              name="first_name"
                              fullWidth
                              value={values.first_name}
                              margin="dense"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.first_name && Boolean(errors.first_name)}
                              helperText={touched.first_name && errors.first_name}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Last Name"
                              name="last_name"
                              fullWidth
                              value={values.last_name}
                              margin="dense"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.last_name && Boolean(errors.last_name)}
                              helperText={touched.last_name && errors.last_name}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Phone Number"
                              name="mobile_no"
                              fullWidth
                              value={values.mobile_no}
                              margin="dense"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.mobile_no && Boolean(errors.mobile_no)}
                              helperText={touched.mobile_no && errors.mobile_no}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Role"
                              fullWidth
                              value={userInfo.role ? userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1) : ''}
                              margin="dense"
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Email"
                              name="email"
                              fullWidth
                              value={values.email}
                              margin="dense"
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              sx={{ mt: 2 }}
                              disabled={isLoading || !dirty || isSubmitting}
                            >
                              {isLoading || isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </Grid>
                        </Grid>
                      </Form>
                    )}
                  </Formik>
                </Box>
              )}
              {tab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Update Password</Typography>
                  <Formik
                    initialValues={{
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: '',
                    }}
                    validationSchema={passwordSchema}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                      try {
                        const resultAction = await dispatch(updateAdminPasswordAction({
                          oldPassword: values.currentPassword,
                          newPassword: values.newPassword
                        }));
                        if (updateAdminPasswordAction.fulfilled.match(resultAction)) {
                          toast.success('Password updated successfully!');
                          resetForm();
                        } else {
                          toast.error(resultAction.payload || 'Password update failed');
                        }
                      } catch (err) {
                        toast.error('Password update failed');
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                      <Form>
                        <Grid container spacing={2}>
                         
                          <Grid item xs={12}>
                            <TextField
                              label="Current Password"
                              name="currentPassword"
                              type={showPassword ? 'text' : 'password'}
                              fullWidth
                              margin="dense"
                              value={values.currentPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.currentPassword && Boolean(errors.currentPassword)}
                              helperText={touched.currentPassword && errors.currentPassword}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="New Password"
                              name="newPassword"
                              type={showPassword ? 'text' : 'password'}
                              fullWidth
                              margin="dense"
                              value={values.newPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.newPassword && Boolean(errors.newPassword)}
                              helperText={touched.newPassword && errors.newPassword}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Confirm New Password"
                              name="confirmPassword"
                              type={showPassword ? 'text' : 'password'}
                              fullWidth
                              margin="dense"
                              value={values.confirmPassword}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                              helperText={touched.confirmPassword && errors.confirmPassword}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'end', gap: 2, mt: 2 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={showPassword}
                                    onChange={e => setShowPassword(e.target.checked)}
                                    color="primary"
                                  />
                                }
                                label="Show Passwords"
                                sx={{ m: 0 }}
                              />
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                                sx={{ minWidth: 170 }}
                              >
                                {isSubmitting ? 'Updating...' : 'Update Password'}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Form>
                    )}
                  </Formik>
                </Box>
              )}
              {tab === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Settings</Typography>
                  <Typography variant="body2" color="text.secondary">Settings content goes here.</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
      {/* No Snackbar needed, toastify handles toasts globally */}
    </>
  );
};

export default ProfilePage;
